import { TableNew, TableRecord } from '@dynamika-shared/ui/TableNew/TableNew';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CohortType } from '@dynamika-shared/types/cohorts';
import { usePaginationAndSort } from '@restore/helpers';
import { VisitConfig } from '@dynamika-shared/types/visits';
import { VisitsControlSchemaType } from '../uiControlSchemaByStatus/ControlTypes';
import { twMerge } from '@utils/twMerge';
import { transformCohorts } from 'src/pages/ClinicalTrial/ClinicalTrialVisits/utils/transformCohorts';
import { transformExpandedRows } from 'src/pages/ClinicalTrial/ClinicalTrialVisits/utils/transformExpandedRows';
import { useAuthSlice } from '@restore/auth/auth.slice';
import { UserRoles } from '@dynamika-shared/types/auth';

export type CohortListProps = {
  cohorts?: CohortType[];
  addVisitAction: (cohort: CohortType) => void;
  deleteVisitAction: (visitId: string) => void;
  deleteCohortAction: (cohort: CohortType) => void;
  shouldBeExpand?: string;
  editCohortAction: (cohort: CohortType) => void;
  editVisitAction: (visit: VisitConfig, cohort: CohortType) => void;
  uiControlSchema: VisitsControlSchemaType;
};
export type OptionType = 'edit' | 'archive' | 'delete';

export type CohortData = readonly (CohortType & { disabled?: boolean })[];

const cohortsColumns = (disabled?: boolean, isPM: boolean = false) => {
  const base = [
    {
      title: 'Name',
      dataIndex: 'name',
      className: 'w-[17.6%] min-w-[17.6%] max-w-[17.6%]',
    },
    {
      title: 'Code Pattern',
      dataIndex: 'patientCodePattern',
      className: 'w-[13.7%] min-w-[13.7%] max-w-[13.7%]',
    },
    {
      title: 'Number of Subjects',
      dataIndex: 'numberOfPatients',
      className: 'w-[16.4%] min-w-[16.4%] max-w-[16.4%]',
    },
    {
      title: 'Number of Visits',
      dataIndex: 'numberOfVisits',
      className: 'w-[15.1%] min-w-[15.1%] max-w-[15.1%]',
    },
    {
      title: 'Unscheduled visits',
      dataIndex: 'unscheduledVisitsAllowed',
      className: 'w-[15.1%] min-w-[15.1%] max-w-[15.1%]',
    },
    { title: 'Status', dataIndex: 'status', className: 'w-[10rem]' }
  ];

  const actionColumn = {
    title: (
      <div className={twMerge('text-right', disabled && 'text-platinum')}>
        Action
      </div>
    ),
    dataIndex: 'action',
    className: '!pr-[2.7rem]',
  };

  return isPM ? base : [...base, actionColumn];
};

export const MAX_LENGTH_NAME = 10;

export const CohortList: FC<CohortListProps> = ({
  cohorts = [],
  addVisitAction,
  editCohortAction,
  shouldBeExpand,
  deleteVisitAction,
  deleteCohortAction,
  editVisitAction,
  uiControlSchema,
}) => {
  const { currentPage, currentPageSize } = usePaginationAndSort('cohorts');

  const dropDownClickHandler = useMemo(
    () => ({
      edit: (cohort: CohortType) => {
        editCohortAction(cohort);
      },
      archive: () => {},
      delete: deleteCohortAction,
    }),
    [deleteCohortAction, editCohortAction]
  );

  const dropDownOnClick = useCallback(
    (option: OptionType, cohort: CohortType) => {
      dropDownClickHandler[option](cohort);
    },
    [dropDownClickHandler]
  );

  const isPM = useAuthSlice(s => s?.data?.selectedRole === UserRoles.PROJECT_MANAGER);

  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const memoizedDataSource = useMemo(
    () =>
      transformCohorts(
        cohorts,
        uiControlSchema,
        addVisitAction,
        dropDownOnClick
      ),
    [
      cohorts,
      uiControlSchema,
      addVisitAction,
      dropDownOnClick,
      currentPage,
      currentPageSize,
    ]
  );

  const expandedRowRender = useCallback(
    transformExpandedRows(uiControlSchema, editVisitAction, deleteVisitAction, isPM),
    [uiControlSchema]
  );

  useEffect(() => {
    const newExpandedRows = expandedRows.filter(
      cohortId => !!cohorts.find(c => c.id === cohortId)?.visits.length
    );
    setExpandedRows(newExpandedRows);
  }, [cohorts]);

  useEffect(() => {
    setExpandedRows(prevState =>
      [...prevState, shouldBeExpand ?? ''].filter(el => el)
    );
  }, [shouldBeExpand]);

  return (
    <div className='h-full'>
      <TableNew<CohortType & TableRecord>
        columns={cohortsColumns(
          uiControlSchema.deleteCohort.hidden ||
            uiControlSchema.editCohort.hidden,
          isPM
        )}
        dataSource={memoizedDataSource}
        expandable={{
          expandedRowRender,
          expandedRowKeys: expandedRows,
          onExpandedRowsChange: rowKeys => setExpandedRows(rowKeys as string[]),
        }}
        pagination={false}
        rowKey={cohort => `${cohort.id}`}
        tableClassName='bg-dodgerBlue main-table mb-2'
      />
    </div>
  );
};
